import { Check, Minus } from "lucide-react";
import { Checkbox as RCheckbox, RadioGroup as RRadio, Switch as RSwitch } from "radix-ui";
import { useId, type ReactNode } from "react";
import { cn } from "../lib/cn";
import type { Option } from "../lib/option";

type ChoiceLabelProps = { label?: ReactNode; description?: ReactNode; id: string; children: ReactNode; className?: string; disabled?: boolean };

/** Строка «контрол + подпись»: вся строка кликабельна, высота ≥ 44px на тач-экранах. */
function ChoiceRow({ label, description, id, children, className, disabled }: ChoiceLabelProps) {
  if (!label) return <>{children}</>;
  return (
    <label htmlFor={id} className={cn("flex min-h-9 cursor-pointer items-start gap-3 py-1 ", disabled && "cursor-not-allowed opacity-55", className)}>
      <span className="pt-0.5">{children}</span>
      <span className="min-w-0 text-sm break-words">
        <span className="font-medium">{label}</span>
        {description && <span className="block text-xs text-muted">{description}</span>}
      </span>
    </label>
  );
}

export type CheckboxProps = {
  checked: boolean | "indeterminate";
  onCheckedChange: (checked: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-label"?: string;
};

export function Checkbox({ checked, onCheckedChange, label, description, disabled, className, id, ...aria }: CheckboxProps) {
  const autoId = useId();
  const cid = id ?? autoId;
  return (
    <ChoiceRow id={cid} label={label} description={description} className={className} disabled={disabled}>
      <RCheckbox.Root
        id={cid}
        checked={checked}
        disabled={disabled}
        onCheckedChange={(c) => onCheckedChange(c === true)}
        aria-label={aria["aria-label"]}
        className="grid size-6 shrink-0 place-items-center rounded-md border-[1.5px] border-border bg-surface text-primary-fg transition-colors data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary"
      >
        <RCheckbox.Indicator>{checked === "indeterminate" ? <Minus className="size-4" /> : <Check className="size-4" />}</RCheckbox.Indicator>
      </RCheckbox.Root>
    </ChoiceRow>
  );
}

export type SwitchProps = Omit<CheckboxProps, "checked"> & { checked: boolean };

export function Switch({ checked, onCheckedChange, label, description, disabled, className, id, ...aria }: SwitchProps) {
  const autoId = useId();
  const cid = id ?? autoId;
  return (
    <ChoiceRow id={cid} label={label} description={description} className={className} disabled={disabled}>
      <RSwitch.Root
        id={cid}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-label={aria["aria-label"]}
        className="relative inline-flex h-6 w-11 shrink-0 items-center rounded-full bg-border transition-colors data-[state=checked]:bg-primary"
      >
        <RSwitch.Thumb className="block size-5 translate-x-0.5 rounded-full bg-white shadow transition-transform data-[state=checked]:translate-x-[22px]" />
      </RSwitch.Root>
    </ChoiceRow>
  );
}

export type RadioGroupProps<V extends string> = {
  value: V | null;
  onValueChange: (value: V) => void;
  options: Option<V>[];
  orientation?: "vertical" | "horizontal";
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
};

export function RadioGroup<V extends string>({ value, onValueChange, options, orientation = "vertical", disabled, className, ...aria }: RadioGroupProps<V>) {
  const base = useId();
  return (
    <RRadio.Root
      value={value ?? ""}
      onValueChange={(v) => onValueChange(v as V)}
      disabled={disabled}
      orientation={orientation}
      aria-label={aria["aria-label"]}
      className={cn("flex gap-x-5 gap-y-1", orientation === "vertical" ? "flex-col" : "flex-wrap", className)}
    >
      {options.map((o) => (
        <ChoiceRow key={o.value} id={`${base}-${o.value}`} label={o.label} description={o.description} disabled={o.disabled || disabled}>
          <RRadio.Item
            id={`${base}-${o.value}`}
            value={o.value}
            disabled={o.disabled}
            className="grid size-6 place-items-center rounded-full border-[1.5px] border-border bg-surface data-[state=checked]:border-primary"
          >
            <RRadio.Indicator className="size-3 rounded-full bg-primary" />
          </RRadio.Item>
        </ChoiceRow>
      ))}
    </RRadio.Root>
  );
}

export type SegmentedControlProps<V extends string> = {
  value: V;
  onValueChange: (value: V) => void;
  options: Option<V>[];
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  className?: string;
  "aria-label"?: string;
};

/** Переключатель режимов (тип заказа «Зал/С собой/Доставка», период отчёта). */
export function SegmentedControl<V extends string>({ value, onValueChange, options, size = "md", fullWidth, className, ...aria }: SegmentedControlProps<V>) {
  const h = { sm: "h-8 text-xs", md: "h-10 text-sm", lg: "h-12 text-base" }[size];
  return (
    <div role="radiogroup" aria-label={aria["aria-label"]} className={cn("inline-flex max-w-full gap-1 overflow-x-auto rounded-full bg-surface-2 p-1", fullWidth && "flex w-full", className)}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={o.disabled}
          onClick={() => onValueChange(o.value)}
          className={cn(
            "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full px-4 font-normal whitespace-nowrap text-muted transition-colors disabled:opacity-50 [&_svg]:size-4",
            h,
            fullWidth && "flex-1",
            value === o.value ? "bg-surface text-fg shadow-[0_1px_3px_rgb(0_0_0/0.08)]" : "hover:text-fg",
          )}
        >
          {o.icon}
          {o.label}
        </button>
      ))}
    </div>
  );
}

export type ColorSwatchesProps<V extends string> = {
  value: V | null;
  onValueChange: (value: V) => void;
  /** `color` — любое CSS-значение цвета кружка */
  options: (Option<V> & { color: string })[];
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
};

/** Выбор цвета из палитры (бренд, метки): кружки-радиокнопки 44px, подпись — у выбранного и для экранного диктора. */
export function ColorSwatches<V extends string>({ value, onValueChange, options, disabled, className, ...aria }: ColorSwatchesProps<V>) {
  const current = options.find((o) => o.value === value);
  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <RRadio.Root value={value ?? ""} onValueChange={(v) => onValueChange(v as V)} disabled={disabled} orientation="horizontal" aria-label={aria["aria-label"]} className="flex flex-wrap gap-2">
        {options.map((o) => (
          <RRadio.Item
            key={o.value}
            value={o.value}
            aria-label={o.label}
            title={o.label}
            disabled={o.disabled}
            className="grid size-11 place-items-center rounded-full border-2 border-transparent outline-none transition-colors focus-visible:ring-2 focus-visible:ring-primary/40 disabled:opacity-50 data-[state=checked]:border-fg"
          >
            <span className="grid size-8 place-items-center rounded-full" style={{ background: o.color }}>
              <RRadio.Indicator className="size-2.5 rounded-full bg-white" />
            </span>
          </RRadio.Item>
        ))}
      </RRadio.Root>
      {current && <p className="text-sm text-muted">{current.label}</p>}
    </div>
  );
}
